// src/components/dnl/ViewPreview.tsx
/**
 * View Preview Component
 * Phase 5: Alternative Views Feature
 * 
 * Shows a preview of what a view will look like before applying it
 */

import React from 'react';
import { Eye, X } from 'lucide-react';
import type { ViewConfiguration } from '../../types/dnl';

export interface ViewPreviewProps {
  /** Whether the preview is open */
  isOpen: boolean;
  /** Which view to preview */
  view: 'A' | 'B';
  /** The view configuration to preview */
  viewConfig: ViewConfiguration;
  /** Callback when user applies the view */
  onApply: () => void;
  /** Callback when user closes preview */
  onClose: () => void;
  /** Dark mode */
  isDark?: boolean;
}

export const ViewPreview: React.FC<ViewPreviewProps> = ({
  isOpen,
  view,
  viewConfig,
  onApply,
  onClose,
  isDark = false,
}) => {
  if (!isOpen) return null;

  const overlayClass = 'fixed inset-0 bg-black/50 flex items-center justify-center z-50';
  const dialogClass = `
    w-full max-w-2xl mx-4 rounded-lg shadow-xl border max-h-[80vh] overflow-hidden flex flex-col
    ${isDark
      ? 'bg-gray-800 border-gray-700 text-white'
      : 'bg-white border-gray-200 text-gray-900'
    }
  `;
  const headerClass = `
    flex items-center justify-between p-4 border-b flex-shrink-0
    ${isDark ? 'border-gray-700' : 'border-gray-200'}
  `;
  const buttonClass = (variant: 'primary' | 'secondary') => `
    px-4 py-2 rounded font-medium transition-colors
    ${variant === 'primary'
      ? isDark
        ? 'bg-blue-600 text-white hover:bg-blue-700'
        : 'bg-blue-500 text-white hover:bg-blue-600'
      : isDark
        ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
    }
  `;

  // Group points by line
  const primaryPoints = viewConfig.points.filter(p => p.line === 'primary');
  const secondaryPoints = viewConfig.points.filter(p => p.line === 'secondary');

  return (
    <div className={overlayClass} onClick={onClose}>
      <div className={dialogClass} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={headerClass}>
          <div className="flex items-center gap-3">
            <Eye size={24} className="text-blue-500" />
            <div>
              <h3 className="font-semibold text-lg">Preview View {view}</h3>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                {view === 'A' ? 'Entity view - each line represents one entity' : 'Measure view - each line represents one type of quantity'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className={`p-1 rounded hover:bg-black/10 ${isDark ? 'hover:bg-white/10' : ''}`}
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Line Labels */}
          <div className="space-y-3">
            <h4 className="font-medium text-sm uppercase tracking-wide opacity-75">
              Line Labels
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div className={`
                p-3 rounded border
                ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}
              `}>
                <div className="text-xs uppercase tracking-wide opacity-75 mb-1">Primary Line</div>
                <div className="font-medium">{viewConfig.primaryLabel || '(No label)'}</div>
                {viewConfig.primarySubtext && (
                  <div className="text-sm opacity-75 mt-1">{viewConfig.primarySubtext}</div>
                )}
              </div>
              <div className={`
                p-3 rounded border
                ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}
              `}>
                <div className="text-xs uppercase tracking-wide opacity-75 mb-1">Secondary Line</div>
                <div className="font-medium">{viewConfig.secondaryLabel || '(No label)'}</div>
                {viewConfig.secondarySubtext && (
                  <div className="text-sm opacity-75 mt-1">{viewConfig.secondarySubtext}</div>
                )}
              </div>
            </div>
          </div>

          {/* Points */}
          <div className="space-y-3">
            <h4 className="font-medium text-sm uppercase tracking-wide opacity-75">
              Custom Points
            </h4>
            <div className="grid grid-cols-2 gap-4">
              {/* Primary Line Points */}
              <div className={`
                p-3 rounded border
                ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}
              `}>
                <div className="text-xs uppercase tracking-wide opacity-75 mb-2">
                  Primary Line ({primaryPoints.length})
                </div>
                {primaryPoints.length > 0 ? (
                  <div className="space-y-1">
                    {primaryPoints.map((point, i) => (
                      <div
                        key={i}
                        className={`
                          text-sm p-2 rounded
                          ${isDark ? 'bg-gray-700' : 'bg-white'}
                        `}
                      >
                        <span className="font-mono">{point.value}</span>
                        {point.label && (
                          <span className="ml-2 opacity-75">({point.label})</span>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm opacity-50 italic">No points</div>
                )}
              </div>

              {/* Secondary Line Points */}
              <div className={`
                p-3 rounded border
                ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}
              `}>
                <div className="text-xs uppercase tracking-wide opacity-75 mb-2">
                  Secondary Line ({secondaryPoints.length})
                </div>
                {secondaryPoints.length > 0 ? (
                  <div className="space-y-1">
                    {secondaryPoints.map((point, i) => (
                      <div
                        key={i}
                        className={`
                          text-sm p-2 rounded
                          ${isDark ? 'bg-gray-700' : 'bg-white'}
                        `}
                      >
                        <span className="font-mono">{point.value}</span>
                        {point.label && (
                          <span className="ml-2 opacity-75">({point.label})</span>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm opacity-50 italic">No points</div>
                )}
              </div>
            </div>
          </div>

          {/* Summary */}
          <div className={`
            p-3 rounded border
            ${isDark
              ? 'bg-blue-900/20 border-blue-700 text-blue-200'
              : 'bg-blue-50 border-blue-200 text-blue-900'
            }
          `}>
            <p className="text-sm">
              <strong>Summary:</strong> This view has {viewConfig.points.length} custom point(s) 
              distributed across {primaryPoints.length} primary and {secondaryPoints.length} secondary line points.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className={`
          flex gap-3 justify-end p-4 border-t flex-shrink-0
          ${isDark ? 'border-gray-700' : 'border-gray-200'}
        `}>
          <button onClick={onClose} className={buttonClass('secondary')}>
            Close
          </button>
          <button onClick={onApply} className={buttonClass('primary')}>
            Apply View {view}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ViewPreview;

